import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, Wrench, Users, Sprout } from "lucide-react";
import usePageTitle from "@/hooks/usePageTitle";

const ZONES = [
  { num: 1, name: "Foundation", color: "#E24B4A", line: "Awareness only" },
  { num: 2, name: "Building", color: "#EF9F27", line: "Developing with guidance" },
  { num: 3, name: "Practising", color: "#CBCB2A", line: "Independent application" },
  { num: 4, name: "Mastering", color: "#639922", line: "High proficiency, teaches others" },
  { num: 5, name: "Pioneering", color: "#C9A84C", line: "Expert, sets the standard" },
];

const DIMS = [
  { name: "Think Power", icon: Brain, emoji: "🧠", desc: "Depth of thinking, problem-solving, and strategic reasoning." },
  { name: "Do Power", icon: Wrench, emoji: "⚙️", desc: "Technical expertise and execution quality in the role." },
  { name: "Connect Power", icon: Users, emoji: "🤝", desc: "Relational intelligence, communication, and collaboration." },
  { name: "Adapt Power", icon: Sprout, emoji: "🌱", desc: "Learning agility and resilience in navigating change." },
];

export default function CapScanLandingPage() {
  usePageTitle("4D CapScan Skills Gap Analysis");
  return (
    <div className="bg-cream min-h-screen">
      {/* Hero */}
      <section className="bg-navy pt-32 pb-16 px-4 relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="inline-block bg-gold/20 text-gold font-dm text-xs px-3 py-1 rounded-full mb-5 font-semibold">
            4D CapScan™
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-playfair text-white text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-tight">
            4D CapScan™ — <span className="text-gold">AI-Powered Intelligence</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="font-dm text-white/70 text-lg max-w-3xl mx-auto mb-2">
            See the full picture. Close the right gaps. Build future-ready teams.
          </motion.p>
          <div className="w-24 h-1 bg-gold mx-auto mt-6 mb-8 rounded-full" />
          <Link to="/capscan/new" data-testid="capscan-hero-cta" className="inline-block bg-gold text-navy font-dm font-bold px-10 py-3 rounded-lg hover:bg-gold/90 transition-colors">
            Start Your CapScan
          </Link>
        </div>
      </section>

      {/* Four dimensions */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="font-playfair text-navy text-3xl font-bold text-center mb-3">The Four Dimensions</h2>
        <p className="font-dm text-navy/60 text-center mb-10">Every role demands a balance of four power dimensions.</p>
        <div className="grid sm:grid-cols-2 gap-5">
          {DIMS.map((d, i) => (
            <motion.div key={d.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="bg-navy border-t-4 border-gold rounded-xl p-7 hover:-translate-y-1 transition-transform" data-testid={`dim-card-${i}`}>
              <div className="text-4xl mb-3">{d.emoji}</div>
              <h3 className="font-playfair text-white text-xl font-bold mb-2">{d.name}</h3>
              <p className="font-dm text-white/70 text-sm leading-relaxed">{d.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Five zones */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-playfair text-navy text-3xl font-bold text-center mb-3">Five Capability Zones</h2>
          <p className="font-dm text-navy/60 text-center mb-10">Every skill is rated on a 5-zone scale.</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {ZONES.map((z) => (
              <div key={z.num} className="text-center bg-cream rounded-xl p-4 border-t-4" style={{ borderColor: z.color }} data-testid={`zone-${z.num}`}>
                <div className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: z.color }}>{z.num}</div>
                <p className="font-dm text-navy font-bold text-sm">{z.name}</p>
                <p className="font-dm text-navy/50 text-[10px] mt-1">{z.line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Token pricing */}
      <section className="max-w-3xl mx-auto px-4 py-16">
        <h2 className="font-playfair text-navy text-2xl font-bold text-center mb-3">Token Pricing</h2>
        <p className="font-dm text-navy/60 text-center text-sm mb-8">Uses intelligence tokens from your account.</p>
        <div className="bg-white rounded-xl shadow-md divide-y divide-navy/10 border-l-4 border-gold">
          {[
            ["Individual CapScan", "2 intelligence tokens"],
            ["Team CapScan (up to 10 people)", "5 intelligence tokens"],
            ["Team CapScan (11–20 people)", "8 intelligence tokens"],
            ["Re-scan (same person)", "1 intelligence token"],
          ].map(([label, cost]) => (
            <div key={label} className="flex items-center justify-between p-4">
              <span className="font-dm text-navy text-sm">{label}</span>
              <span className="font-dm text-gold font-bold text-sm">{cost}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA repeat */}
      <section className="bg-gold py-12 text-center">
        <h2 className="font-playfair text-navy text-2xl font-bold mb-3">Ready to map your team's capability?</h2>
        <Link to="/capscan/new" data-testid="capscan-bottom-cta" className="inline-block bg-navy text-gold font-dm font-bold px-10 py-3 rounded-lg hover:bg-navy/90 transition-colors">
          Start Your CapScan
        </Link>
      </section>
    </div>
  );
}
